/**
 * engine/element.ts — the two audio elements of the application: the decks A and B.
 *
 * Task 07 made one element; task 45 made it two. `spec.md` «Микс и дополнения плеера», «Две
 * деки»: while one deck plays a track, the other makes the next one ready, and one track goes
 * into the next as two volumes moving. This module is the single place that may create an
 * element: the `fdm/no-new-audio` rule names this path as its exception (beside the probe page
 * of task 42), and `test/audio-element.test.ts` checks the source of `apps/web/src` for exactly
 * two. That is why no comment in `apps/web/src` spells out a construction form.
 *
 * ## Why created outside React, once
 *
 * A browser puts its autoplay restriction on each element **when it is created**, and the
 * first `play()` inside a tap lifts it **for that element, for good** — not for the page, and
 * it is not put back when `src` changes. An element per track, or an `<audio>` that React
 * unmounts and mounts again, is locked again every time, and every `play()` after the first
 * track fails with `NotAllowedError`. The failure is partial, which makes it nasty: the
 * computer is fine, the first track on the phone is fine, and nothing after it plays. So both
 * decks are made here once, and the first tap unlocks both (`engine/player.ts`).
 *
 * ## Two, not one, since task 45
 *
 * Until task 45 this header said that two live elements compete for the one audio focus of the
 * Telegram webview — «iOS silences one, Android pauses both». Nobody had shown it. The probe of
 * task 42 played two at once on an iPhone 15 Pro Max inside Telegram, minimised and on the lock
 * screen, and the iOS half is gone (`docs/verification-log.md`, 2026-10-04). The Android half is
 * **not checked**: the owner has no Android phone, and checks it on a borrowed one before task
 * 45 is deployed.
 *
 * The elements are not put into the document. An element outside the document plays the same,
 * and no unmount can take it down with it.
 *
 * ## Their settings
 *
 * - `preload = 'none'`: nothing is fetched before the first tap. On a metered connection a
 *   speculative download of a mix is a real cost. `unlock.ts` raises it once unlocked.
 * - `playsInline`: WebKit never considers a fullscreen presentation. It is typed on the video
 *   element only, hence the narrow cast.
 * - `crossOrigin` is **not** set: the sound is same-origin, and opting into CORS would make the
 *   element's requests differ from any other request for the same URL and split the cache. Web
 *   Audio reads a same-origin element without it.
 *
 * Everything else in `engine/` takes a {@link MediaElementLike}, so a test drives it with a
 * recording fake (`test/helpers/fake-audio.ts`); {@link deckA} and {@link deckB} are the
 * production default.
 */

/** The `TimeRanges` surface read here. Structural: a real one cannot be built from script. */
export interface TimeRangesLike {
  readonly length: number;
  start(index: number): number;
  end(index: number): number;
}

/** The part of `HTMLMediaElement` the engine touches. */
export interface MediaElementLike {
  src: string;
  currentTime: number;
  readonly duration: number;
  readonly paused: boolean;
  readonly ended: boolean;
  preload: string;
  readonly readyState: number;
  readonly buffered: TimeRangesLike;
  readonly seekable: TimeRangesLike;
  readonly error: { readonly code: number } | null;
  /** Task 09: the record's speed (`engine/speed.ts`). `0` is silence without a pause. */
  playbackRate: number;
  /** Task 09: off, the pitch follows the speed like a record's. */
  preservesPitch: boolean;
  /** WebKit before Safari 17 knows the pitch switch only under this name. */
  webkitPreservesPitch?: boolean;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

type InlinePlayable = { playsInline: boolean };

function settle(element: HTMLAudioElement): HTMLAudioElement {
  element.preload = 'none';
  (element as HTMLAudioElement & InlinePlayable).playsInline = true;
  return element;
}

/** Deck A: the one the first track plays on. Never replaced, never put in the document. */
export const deckA: HTMLAudioElement = settle(new Audio());
/** Deck B: the one the next track is made ready on. Never replaced, never put in the document. */
export const deckB: HTMLAudioElement = settle(new Audio());
